/// <amd-module name="VSS/Features/Search/SearchInput/SearchInput" />
import * as React from "react";
import * as Resources from "VSS/Features/Search/Resources";

import { Observable } from "VSS/Core/Observable";

import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureEnabled } from "VSS/Platform/Feature";
import { css, IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IContributedSearchProvider, ISearchInput, ISearchPrefixProps, ISearchProvider, ISearchQuery, IVssSearchService } from "VSS/Platform/Search";

import { ICalloutProps } from "VSSUI/Callout";
import { FocusZoneDirection } from "VSSUI/FocusZone";
import { Icon, IconSize } from "VSSUI/Icon";
import { ContextualMenu, IMenuItem } from "VSSUI/Menu";
import { Pill, PillSize } from "VSSUI/Pill";
import { Location } from "VSSUI/Utilities/Position";

import { ISearchInputComponentProps, ISearchInputComponentState } from "./SearchInput.Props";

export const enum SearchInputEvents {
    TextChange = "change",
    KeyDown = "keyDown"
}

class SearchInput extends Observable<string> implements ISearchInput {
    public appendText: (text: string, triggerChange?: boolean) => void;
    public queryText: () => string;
    public replaceText: (text: string, triggerChange?: boolean) => void;
    public setHelpVisibility: (hideHelp: boolean, focusInputOnShow?: boolean) => void;
    public setHelpSearchProviderId: (helpSearchProviderId: string) => void;
    public executeSearch: (openInNewWindow?: boolean) => void;
    public onSearchPrefixRemove: () => void;
    public setSearchPrefixProps: (searchPrefixProps: ISearchPrefixProps) => void;
    public setPlaceholder: (placeholder?: string) => void;

    constructor(
        appendText: (text: string, triggerChange?: boolean) => void,
        queryText: () => string,
        replaceText: (text: string, triggerChange?: boolean) => void,
        setHelpVisibility: (hideHelp: boolean, focusInputOnShow?: boolean) => void,
        setHelpSearchProviderId: (helpSearchProviderId: string) => void,
        executeSearch: (openInNewWindow?: boolean) => void,
        onSearchPrefixRemove: () => void,
        setSearchPrefixProps: (searchPrefixProps: ISearchPrefixProps) => void,
        setPlaceholder: (placeholder?: string) => void
    ) {
        super();

        this.appendText = appendText;
        this.queryText = queryText;
        this.replaceText = replaceText;
        this.setHelpVisibility = setHelpVisibility;
        this.setHelpSearchProviderId = setHelpSearchProviderId;
        this.executeSearch = executeSearch;
        this.onSearchPrefixRemove = onSearchPrefixRemove;
        this.setSearchPrefixProps = setSearchPrefixProps;
        this.setPlaceholder = setPlaceholder;
    }

    public textChanged(value: string): void {
        this.notify(value, SearchInputEvents.TextChange);
    }

    public keyDown(value: string): void {
        this.notify(value, SearchInputEvents.KeyDown);
    }
}

/**
 * This implements a search input component that uses the platform search service to
 * define the set of available search providers.
 */
export class SearchInputComponent extends VssComponent<ISearchInputComponentProps, ISearchInputComponentState> {
    private availableProviders: { [providerId: string]: IContributedSearchProvider } = {};
    private searchElement: React.RefObject<HTMLDivElement>;
    private inputElement: React.RefObject<HTMLInputElement>;
    private helpElement: React.RefObject<HTMLDivElement>;
    private clearButton: React.RefObject<HTMLDivElement>;
    private searchInput: SearchInput;

    constructor(props: ISearchInputComponentProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {};
        this.state = this.processProviders(props);
        this.searchElement = React.createRef<HTMLDivElement>();
        this.inputElement = React.createRef<HTMLInputElement>();
        this.helpElement = React.createRef<HTMLDivElement>();
        this.clearButton = React.createRef<HTMLDivElement>();
    }

    public render(): JSX.Element | null {
        const {
            contentAvailable,
            hideHelp,
            hasFocus,
            shownProvider,
            providerMenuItems,
            placeholderText,
            selectedProviderId,
            searchPrefixProps
        } = this.state;
        const placeholder =
            placeholderText ||
            this.props.placeholderText ||
            (selectedProviderId && this.availableProviders[selectedProviderId] && this.availableProviders[selectedProviderId].placeholder) ||
            Resources.SearchPlaceholder;
        const queryText = this.inputElement.current ? this.inputElement.current.value : "";
        const showClearTextButton = queryText || searchPrefixProps;

        let helpComponent: JSX.Element | null = null;
        // Draw the help popup the help is not hidden, the control has focus, and we have
        // loaded the shownProvider and the shownProvider has help.
        if (!hideHelp && hasFocus && shownProvider && shownProvider.help) {
            const providerHelpElement = shownProvider.help(this.searchInput);
            if (providerHelpElement) {
                const { dropdownGapSize, helpId, helpClassName } = this.props;
                const isSearchSuggestionsEnabled = isFeatureEnabled(this.context.pageContext, "ms.vss-tfs-web.enable-search-suggestions", false);
                const spaceOnRight = this.searchElement.current
                    ? window.innerWidth - this.searchElement.current.getBoundingClientRect().right - 4
                    : 0;
                const horizontalGapSize = isSearchSuggestionsEnabled ? spaceOnRight : dropdownGapSize;
                helpComponent = (
                    <WrappedComponent
                        dependencies={["ms.vss-features.ui-content"]}
                        wrappedType="callout"
                        {...({
                            anchorOffset: { horizontal: horizontalGapSize, vertical: dropdownGapSize },
                            anchorElement: this.searchElement.current,
                            anchorOrigin: { horizontal: Location.end, vertical: Location.end },
                            calloutOrigin: { horizontal: Location.end, vertical: Location.start },
                            className: "search-help-callout search-callout",
                            contentShadow: true,
                            focuszoneProps: {
                                focusOnMount: false,
                                disabled: false,
                                direction: FocusZoneDirection.Vertical,
                                circularNavigation: true
                            }
                        } as ICalloutProps)}
                    >
                        <div id={helpId} className={helpClassName} ref={this.helpElement} onKeyDown={this.onKeyDown_Help}>
                            {providerHelpElement}
                        </div>
                    </WrappedComponent>
                );
            }
        }

        const { ariaLabel, inputAriaLabel, maxLength, showSearchIcon, showSearchProviders, spellCheck } = this.props;
        const widthOnFocus = hasFocus && this.props.widthOnFocus ? { width: this.props.widthOnFocus + "px" } : undefined;

        return selectedProviderId ? (
            <div
                ref={this.searchElement}
                className={css("flex-cell", "search", hasFocus ? " focus" : "")}
                onBlur={this.onBlur_Component}
                style={widthOnFocus}
                onFocus={this.onFocus_Component}
                aria-label={ariaLabel}
                role="search"
            >
                {showSearchIcon && !searchPrefixProps && (
                    <div
                        className="search-icon cursor-pointer"
                        onClick={this.onSearchClicked}
                        onKeyDown={this.onKeyDown_Search}
                        role="button"
                        tabIndex={contentAvailable ? 0 : undefined}
                        title={Resources.ExecuteSearch}
                    >
                        <Icon iconName="Search" />
                    </div>
                )}
                {searchPrefixProps && (
                    <Pill
                        size={PillSize.regular}
                        className={"flex-noshrink"}
                        iconProps={{ iconName: searchPrefixProps.iconName }}
                        excludeTabStop={true}
                    >
                        {searchPrefixProps.prefixText}
                    </Pill>
                )}
                <input
                    autoComplete="off"
                    ref={this.inputElement}
                    className="search-input"
                    id="l1-search-input"
                    onChange={this.onChange}
                    onFocus={this.onFocus_Input}
                    onKeyDown={this.onKeyDown_Input}
                    placeholder={placeholder}
                    spellCheck={spellCheck}
                    aria-label={inputAriaLabel || placeholder}
                    type="text"
                    maxLength={maxLength}
                />
                {showClearTextButton && (
                    <div
                        ref={this.clearButton}
                        className="flex-row clear-text-icon cursor-pointer"
                        role="button"
                        tabIndex={contentAvailable ? 0 : undefined}
                        onKeyDown={this.onKeyDown_ClearText}
                        title={Resources.ClearText}
                        onClick={this.clearInput}
                    >
                        <Icon iconName="ChromeClose" size={IconSize.small} />
                    </div>
                )}
                {showSearchProviders && Object.keys(this.availableProviders).length > 1 ? (
                    <div
                        aria-owns={providerMenuItems && "search-providers-menu"}
                        className="search-icon"
                        onClick={this.showProviderMenu}
                        onKeyDown={this.onKeyDown_Provider}
                        role="button"
                        tabIndex={0}
                        title={Resources.SearchChangeType}
                    >
                        <span className="ms-Icon ms-Icon--ChevronDown ms-SearchBox-icon" />
                    </div>
                ) : null}
                {helpComponent}
                {providerMenuItems && this.searchElement.current ? (
                    <ContextualMenu
                        anchorElement={this.searchElement.current}
                        menuProps={{
                            id: "search-providers-menu",
                            items: providerMenuItems
                        }}
                        onDismiss={this.onProvidersDismiss}
                    />
                ) : null}
            </div>
        ) : null;
    }

    public componentDidMount(): void {
        super.componentDidMount();

        const searchService = this.context.pageContext.getService<IVssSearchService>("IVssSearchService");
        searchService.subscribe(this.onProviderChanged, "providerSelected");

        if (this.props.initialQueryText) {
            this.setQueryText(this.props.initialQueryText);
        }

        this.searchInput = new SearchInput(
            (text: string, triggerChange?: boolean) => {
                this.replaceOrAppendText(text, true, triggerChange);
            },
            (): string => {
                return this.inputElement.current !== null ? this.inputElement.current.value : "";
            },
            (text: string, triggerChange?: boolean) => {
                this.replaceOrAppendText(text, false, triggerChange);
            },
            (hideHelp: boolean, focusInputOnShow?: boolean) => {
                this.setState({ hideHelp: hideHelp, hasFocus: hideHelp ? this.state.hasFocus : true }, () => {
                    if (!hideHelp && focusInputOnShow) {
                        this.focus(); // Focus in input box as well when showing help
                    }
                });
            },
            (helpSearchProviderId: string) => {
                if (helpSearchProviderId) {
                    const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
                    contributionService.getService<ISearchProvider>(helpSearchProviderId).then(searchProvider => {
                        if (searchProvider) {
                            this.setState({
                                helpSearchProviderId: helpSearchProviderId,
                                helpSearchProvider: searchProvider
                            });
                        }
                    });
                }
            },
            (openInNewWindow?: boolean) => {
                this.executeSearch(openInNewWindow);
            },
            () => {},
            searchPrefixProps => {
                this.setSearchPrefixProps(searchPrefixProps);
            },
            placeholder => {
                this.setPlaceholder(placeholder);
            }
        );

        if (this.props.componentRef) {
            this.props.componentRef(this);
        }
    }

    public componentWillUnmount(): void {
        const searchService = this.context.pageContext.getService<IVssSearchService>("IVssSearchService");
        searchService.unsubscribe(this.onProviderChanged, "providerSelected");

        super.componentWillUnmount();
    }

    public UNSAFE_componentWillReceiveProps(nextprops: ISearchInputComponentProps): void {
        if (nextprops.selectedProviderId && nextprops.selectedProviderId !== this.state.selectedProviderId) {
            this.setState(this.processProviders(nextprops));
        }
    }

    public hasFocus(): boolean | undefined {
        return this.state.hasFocus;
    }

    public focus(setCursorAtEnd?: boolean): void {
        const inputElement = this.inputElement.current;
        if (inputElement) {
            inputElement.focus();
            const length = inputElement.value.length;
            if (setCursorAtEnd) {
                this.setSelection(length, length);
            } else {
                this.setSelection(0, length);
            }
        }
    }

    public blur(): void {
        if (this.inputElement.current) {
            this.inputElement.current.blur();
        }
    }

    public setQueryText(queryText: string): void {
        if (this.inputElement.current && this.inputElement.current.value !== queryText) {
            this.inputElement.current.value = queryText;
        }
    }

    public getQueryText(): string {
        if (this.inputElement.current) {
            return this.inputElement.current.value;
        }
        return "";
    }

    public setState<K extends keyof ISearchInputComponentState>(state: Pick<ISearchInputComponentState, K>, callback?: () => void): void {
        super.setState(state, (): void => {
            // Due to delayed changes in focus and state changes if we render and detect the current
            // state is out of date, we will force a provider load.
            if (this.state.selectedProviderId && this.state.selectedProviderId != this.state.shownProviderId) {
                const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
                const selectedProviderId = this.state.selectedProviderId;

                // Retrieve the search provider to show their help.
                contributionService.getService<ISearchProvider>(selectedProviderId).then(searchProvider => {
                    if (searchProvider) {
                        this.setState({ shownProvider: searchProvider, shownProviderId: selectedProviderId });
                    } else {
                        // @TODO: Notify the user we cant load the search [provider.]
                    }
                });
            }

            if (callback) {
                callback();
            }
        });
    }

    private setSelection(start: number, end: number): void {
        const inputElement = this.inputElement.current;
        if (inputElement) {
            if (isEdge()) {
                // Edge sets cursor at the end of the text by default. So, we need to
                // override that behavior and set it to start manually to select existing text.
                inputElement.selectionStart = start;
            }

            // Need to schedule selection later to avoid script override
            // on focus and make it work in all browsers.
            setTimeout(() => {
                inputElement.selectionStart = start;
                inputElement.selectionEnd = end;
            }, 0);
        }
    }

    private executeSearch(openInNewWindow?: boolean): void {
        if (this.inputElement.current !== null) {
            const inputText = this.inputElement.current.value;
            let finalProviderId = this.state.shownProviderId;
            let finalProvider = this.state.shownProvider;

            if (this.state.helpSearchProvider && this.state.helpSearchProviderId) {
                finalProvider = this.state.helpSearchProvider;
                finalProviderId = this.state.helpSearchProviderId;
            }

            if (finalProviderId && finalProvider) {
                const queryText = inputText;

                // If the selected provider has a default query transform we will use it before
                // executing the query, otherwise we will go with the basic query.
                let searchQuery: ISearchQuery = { queryText: queryText, providerId: finalProviderId, openInNewWindow: openInNewWindow };
                if (finalProvider.getQuery) {
                    searchQuery = finalProvider.getQuery(searchQuery);
                }

                // Call performAction if it is implemented by the provider before executeSearch can be called.
                // Based on the whether action is performed or not, call executeSearch.
                if (finalProvider.performAction) {
                    finalProvider.performAction(searchQuery).then(
                        actionPerformed => {
                            this.setState({ hideHelp: true }); // Hide any help after executing the search.

                            if (!actionPerformed) {
                                this.executeSearchForQueryText(searchQuery);
                            }
                        },
                        () => this.executeSearchForQueryText(searchQuery)
                    );
                } else {
                    this.executeSearchForQueryText(searchQuery);
                }
            }
        }
    }

    private executeSearchForQueryText(searchQuery: ISearchQuery): void {
        if (searchQuery.queryText) {
            this.props.executeSearch(searchQuery);
        }
    }

    private processProviders(props: ISearchInputComponentProps): Partial<ISearchInputComponentState> {
        // Get the set of contributed search providers that are avialable for this search input.
        const searchService = this.context.pageContext.getService<IVssSearchService>("IVssSearchService");
        const searchProviders = searchService.getProviders();

        let customProviderSelected: boolean | undefined;
        let selectedProviderId: string | undefined;

        // Build the list of providers available to the searchInput.
        this.availableProviders = {};
        for (const providerId in searchProviders) {
            if (!props.availableProviderIds || props.availableProviderIds.indexOf(providerId) != -1) {
                this.availableProviders[providerId] = searchProviders[providerId];
            }
        }

        // If an initial provider was specified attempt to select that provider.
        if (props.selectedProviderId && this.availableProviders[props.selectedProviderId]) {
            selectedProviderId = props.selectedProviderId;
            customProviderSelected = true;
        }

        // If no initial provider has been determined yet we need to select one.
        if (!customProviderSelected || !selectedProviderId) {
            const selectedProvider = searchService.getSelectedProvider();
            if (selectedProvider) {
                selectedProviderId = selectedProvider;
            }
        }

        return {
            customProviderSelected: customProviderSelected,
            selectedProviderId: selectedProviderId
        };
    }

    private setPlaceholder = (placeholderText?: string): void => {
        this.setState({ placeholderText });
    };

    private setSearchPrefixProps = (searchPrefixProps?: ISearchPrefixProps): void => {
        this.setState({ searchPrefixProps });
    };

    private onBlur_Component = (event: React.FocusEvent<HTMLDivElement>) => {
        const focusTarget = (event.relatedTarget as HTMLElement) || document.activeElement;
        this.setState({
            hasFocus:
                (this.searchElement.current && this.searchElement.current.contains(focusTarget)) ||
                (this.helpElement.current && this.helpElement.current.contains(focusTarget)) ||
                !!this.state.providerMenuItems,
            hideHelp: !(
                (this.inputElement.current && this.inputElement.current.contains(focusTarget)) ||
                (this.helpElement.current && this.helpElement.current.contains(focusTarget)) ||
                (this.clearButton.current && this.clearButton.current.contains(focusTarget))
            )
        });
    };

    private onChange = () => {
        if (this.inputElement.current !== null) {
            const contentAvailable = this.inputElement.current.value.length > 0;

            // Show the help component whenever the user is typing.
            this.setState({
                hideHelp: false,
                contentAvailable: contentAvailable,
                hasFocus: true,
                helpSearchProviderId: undefined,
                helpSearchProvider: undefined // reset the suggested provider if input changes
            });
            this.searchInput.textChanged(this.inputElement.current.value);
        }
    };

    private onFocus_Component = () => {
        if (!this.state.hasFocus) {
            this.setState({ hasFocus: true });
        }
    };

    private onFocus_Input = () => {
        if (!this.state.hasFocus) {
            this.setState({ hasFocus: true, hideHelp: false });
            if (this.props.onInputFocus) {
                this.props.onInputFocus();
            }
        }
    };

    private onProvidersDismiss = () => {
        this.setState({
            hasFocus: !!this.searchElement.current && this.searchElement.current.contains(document.activeElement),
            hideHelp: false,
            providerMenuItems: undefined
        });
    };

    private onKeyDown_Help = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.which === 27 /* KeyCode.Escape */) {
            this.focus();
            if (!this.state.hideHelp) {
                this.setState({ hideHelp: true });
                event.preventDefault();
            }
        } else if (event.which === 38 /* KeyCode.UpArrow*/) {
            const focusTarget = (event.target as HTMLElement) || document.activeElement;
            const firstFocusElementInHelpDropdown = this._getFirstFocusElementInHelpDropdown();
            if (firstFocusElementInHelpDropdown === focusTarget) {
                this.focus();
                // When up arrow is pressed from first focusable element in help dropdown, we want to focus
                // in input box. So, calling stopPropagation to avoid circular focus trap.
                event.stopPropagation();
            }
        }
    };

    private onKeyDown_Input = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13 /* KeyCode.Return */) {
            this.blur();
            this.executeSearch(event.ctrlKey);
        } else if (event.which === 27 /* KeyCode.Escape */) {
            if (!this.state.hideHelp) {
                this.setState({ hideHelp: true });
                event.preventDefault();
            }
        } else if (event.which === 40 /* KeyCode.DownArrow */) {
            if (this.sendKeysToHelp()) {
                this.searchInput.keyDown(event.key);
            } else {
                const firstFocusElement = this._getNextToFocusedElement() || this._getFirstFocusElementInHelpDropdown();
                if (firstFocusElement) {
                    firstFocusElement.focus();
                }

                event.stopPropagation();
                event.preventDefault();
            }
        } else if (event.which === 38 /* KeyCode.UpArrow*/) {
            if (this.sendKeysToHelp()) {
                this.searchInput.keyDown(event.key);
                event.preventDefault(); // So that on up key doesnt change cursor position
            }
        } else if (event.which === 8 /* KeyCode.Backspace */ && !this.searchInput.queryText() /* Search text empty */) {
            this._removeSearchPrefix();
        }
    };

    private _removeSearchPrefix(): void {
        const { searchPrefixProps } = this.state;
        const { onSearchPrefixRemove } = this.searchInput;
        if (searchPrefixProps) {
            this.setState({ searchPrefixProps: undefined });
            if (onSearchPrefixRemove) {
                onSearchPrefixRemove();
            }
        }
    }

    private _getNextToFocusedElement(): HTMLElement | null {
        if (this.helpElement.current) {
            return this.helpElement.current.querySelector("tr.focused ~ tr[tabIndex='-1']");
        }

        return null;
    }

    private _getFirstFocusElementInHelpDropdown(): HTMLElement | null {
        if (this.helpElement.current) {
            return (
                (this.helpElement.current.querySelector("li[tabIndex='-1']") as HTMLElement | null) ||
                (this.helpElement.current.querySelector("tr[tabIndex='-1']") as HTMLElement | null) ||
                (this.helpElement.current.querySelector("div[tabIndex='-1']") as HTMLElement | null)
            );
        }

        return null;
    }

    private sendKeysToHelp = () => {
        return this.props.sendKeysToHelp === true;
    };

    private onKeyDown_Provider = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.which === 13 /* KeyCode.Enter */ || event.which === 32 /* KeyCode.Space */ || event.which === 40 /* KeyCode.DownArrow */) {
            this.showProviderMenu();
        }
    };

    private onKeyDown_Search = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.which === 13 /* KeyCode.Enter */ || event.which === 32 /* KeyCode.Space */) {
            this.executeSearch(event.ctrlKey);
        }
    };

    private onProviderChanged = (selectedProviderId: string): void => {
        this.setState({ customProviderSelected: true, selectedProviderId: selectedProviderId });
    };

    private onSearchClicked = (event: React.MouseEvent<HTMLElement>) => {
        this.executeSearch(event.ctrlKey);
    };

    private showProviderMenu = () => {
        const menuItems: IMenuItem[] = [];

        for (const providerId of Object.keys(this.availableProviders)) {
            if (providerId != this.state.selectedProviderId) {
                menuItems.push({
                    id: providerId,
                    onActivate: () => {
                        this.setState({ customProviderSelected: true, selectedProviderId: providerId });
                        this.focus();
                    },
                    text: this.availableProviders[providerId].providerName
                });
            }
        }

        this.setState({ hideHelp: true, providerMenuItems: menuItems });
    };

    private onKeyDown_ClearText = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.which === 13 /* KeyCode.Enter */ || event.which === 32 /* KeyCode.Space */) {
            this.clearInput();
        }
    };

    private clearInput = (): void => {
        this._removeSearchPrefix();
        this.replaceOrAppendText("", false);
    };

    private replaceOrAppendText = (text: string, append: boolean = true, triggerChange: boolean = true, suggestedProviderId?: string): void => {
        if (this.inputElement.current !== null) {
            // Replace the current text with the updated one if doesn't need to be appended.
            const newText = append ? this.inputElement.current.value + (this.inputElement.current.value.length > 0 ? " " : "") + text : text;
            this.inputElement.current.value = newText;

            // Modifying the value directly doesnt fire change events so make sure we
            // have the proper contentAvailable state.
            this.setState({ contentAvailable: true });

            if (triggerChange) {
                this.searchInput.textChanged(this.inputElement.current.value);
            }
            this.focus(true);
        }
    };
}

export function isIE(): boolean {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const msie = /msie ([\d+.]+)/i.exec(navigator.userAgent);

    return !!msie || userAgent.indexOf("trident") !== -1;
}

export function isEdge(): boolean {
    const edge = /edge\/([\d+.]+)/i.exec(navigator.userAgent);
    return !!edge;
}
